/**
 * Helpers for descriptions saved by the rich-text editor.
 *
 * Depending on where and when a description was saved it can be plain text, HTML ("<p>Hot &amp; spicy</p>")
 * or the editor's own JSON (Draft.js raw content). An untouched editor saves an EMPTY document such as
 * "<p></p>", which is not an empty string, so a simple falsy check treats it as a real description.
 */

const HTML_TAG = /<\/?[a-z][\s\S]*>/i;
// Tags that are content even though they hold no text (the editor has a media control).
const MEDIA_TAG = /<(img|video|audio|iframe)\b/i;

/** The editor's JSON form, or null when `value` is not that. */
function parseDraftRaw(value) {
    if (typeof value !== "string" || value.charAt(0) !== "{") return null;
    try {
        const raw = JSON.parse(value);
        return raw && Array.isArray(raw.blocks) ? raw : null;
    } catch (e) {
        return null;
    }
}

function htmlToText(html) {
    const doc = new DOMParser().parseFromString(
        // Block boundaries and <br> become spaces so "<p>a</p><p>b</p>" reads "a b", not "ab".
        String(html).replace(/<\s*br\s*\/?>/gi, " ").replace(/<\/(p|div|li|h[1-6]|tr)>/gi, " $& "),
        "text/html"
    );
    // Their contents are code, not description text, and textContent would include them.
    doc.querySelectorAll("script, style").forEach((node) => node.remove());
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

// Text that is nothing but tags, e.g. the characters "<p></p>". That is what the editor used to save when it
// was opened on an empty description (see getEditorDefaultValue in BusinessDetailSection): the HTML was
// loaded as typed text, so it now sits in the database as "<p>&lt;p&gt;&lt;/p&gt;</p>". It is not a description.
const ONLY_TAGS = /^(?:\s*<\/?[a-z][^<>]*>\s*)+$/i;
const withoutEmptyMarkup = (text) => (ONLY_TAGS.test(text) ? "" : text);

/** The description as plain single-line text, exactly what was typed, without markup. */
export function richTextToPlainText(value) {
    if (value === undefined || value === null) return "";
    const text = String(value);
    const draft = parseDraftRaw(text);
    if (draft) return withoutEmptyMarkup(draft.blocks.map((b) => (b && b.text) || "").join(" ").replace(/\s+/g, " ").trim());
    if (HTML_TAG.test(text)) return withoutEmptyMarkup(htmlToText(text));
    return text.trim();
}

/** False for undefined, "", whitespace, and empty editor documents such as "<p></p>" or "<p><br></p>". */
export function hasRichTextContent(value) {
    if (value === undefined || value === null) return false;
    const text = String(value);
    const draft = parseDraftRaw(text);
    if (draft) return richTextToPlainText(text) !== "" || Object.keys(draft.entityMap || {}).length > 0;
    if (HTML_TAG.test(text)) return richTextToPlainText(text) !== "" || MEDIA_TAG.test(text);
    return text.trim() !== "";
}
